import {
    Alert,
    Button,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Row,
    Switch,
    Tooltip,
    Typography
} from "antd";
import {FormDataValues, FormItemClassNames, formItemLayout, getLabelInValue, tailFormItemLayout} from "common/form";
import {LocationSelect} from "components/form/select/LocationSelect";
import {Link, useLocation} from "react-router-dom";
import React from "react";
import {momentOrNull} from "common/util";
import moment from "moment";
import { useSubmit } from "./actions";
import { useRemoveFromFlight } from "../actions";
import DateTimePicker, {DateTimePickerValue} from "components/date-time-picker";
import {FormData, FormProps, PaxNodeFields} from "./types";
import {WrappedFormUtils} from "antd/lib/form/Form";
import {TooltipTrigger} from "antd/lib/tooltip";
import useUserGroups from "../../../hooks/useUserGroups";
import {hasEditPermission} from "../util";
import {useApolloClient} from "react-apollo";
import {PassengerQuery, SetFlytCheckStatus} from "../graphql";
import {FLYTCHECK_PERMISSION, isFlytChecked} from "./flytcheck";
import {CHECK_IN_PERMISSION, getCheckInStateUpdate, getCheckInTimeDisabledReason, isCheckedInPassenger} from "./check-in-state";

// Ant Design 3 forwards trigger arrays to rc-tooltip, but its types only declare a single trigger.
const checkInTooltipTriggers = ['hover', 'focus'] as unknown as TooltipTrigger;

export interface CheckinDetailsProps {
    editingEnabled: boolean,
    initialData: PaxNodeFields,
    formData?: FormData,
    onFormDataChanged?: (newData: FormData) => void,
    onFormValuesChanged?: (newValues: FormDataValues<FormData>) => void,
    onPassengerUpdated?: (paxNode: PaxNodeFields) => void,
    onSubmitStatusUpdated?: (isSubmitting: boolean) => void,
    onRemoveFromFlight?: () => void,
    isRemovingFromFlight?: boolean
}

interface InternalFormProps extends FormProps {
    isUpdating: boolean,
    isFlytCheckUpdating: boolean,
    onFormSubmit: (form: WrappedFormUtils<PaxNodeFields>) => void,
    onFlytCheckChange: (checked: boolean) => void,
    userGroups: ReturnType<typeof useUserGroups>[0],
    routerLocation: any
}

class CheckinDetails_Internal extends React.Component<CheckinDetailsProps & InternalFormProps>{

    isEditing = () => this.props.editingEnabled

    getCheckedInTime = () => {
        const cit = momentOrNull(this.props.initialData.checkedInTime);

        // FlytDesk also stores the placeholder as local midnight without an offset.
        // Compare the calendar day as well as the UTC instant so timezone conversion
        // cannot turn that placeholder into an apparent check-in time.
        if (!cit?.isValid() || cit.isSameOrBefore(moment('1970-01-01'), 'day') ||
            cit.isSameOrBefore(moment.utc('1970-01-01T00:00:00Z'))){
            return null;
        }

        return cit;
    }

    handleFormSubmit = () => {
        if (typeof this.props.onFormSubmit == 'function'){
            this.props.onFormSubmit(this.props.form);
        }
    }

    onRemoveFromFlight = () => {
        if (typeof this.props.onRemoveFromFlight === 'function'){
            this.props.onRemoveFromFlight();
        }
    }

    isScheduling = () => (this.props.routerLocation.pathname.startsWith('/app/scheduling'))

    render() {
        const paxNode = this.props.initialData;

        if (!paxNode) return null;

        const isOnFlight = !!paxNode.currentCarrierDoc;

        const {form: {getFieldDecorator, getFieldValue}} = this.props;

        let onFlightAlert = null;

        let hasPermission = (
            hasEditPermission(this.props.routerLocation.pathname, this.props.userGroups)
        )

        const editingDisabled = !this.isEditing() || !hasPermission;
        const stateDisabledReason = getCheckInTimeDisabledReason(paxNode);
        const canEditCheckInTime = !editingDisabled && this.props.userGroups.includes(CHECK_IN_PERMISSION) && !stateDisabledReason;
        const checkInDisabledReason = !hasPermission || !this.props.userGroups.includes(CHECK_IN_PERMISSION) ? 'You do not have permission to check in passengers.'
            : !this.isEditing() ? 'Passenger details are read-only.'
            : this.props.isUpdating ? 'Wait for the current save to finish.'
            : stateDisabledReason;
        const checkedInDateTime = DateTimePickerValue.toMoment(getFieldValue('checkedInTime') || DateTimePickerValue.from(this.getCheckedInTime()));
        const checkInTime = checkedInDateTime?.toISOString();
        const scheduledDateValue = getFieldValue('scheduledFlightDate');
        const scheduledFlightDate = scheduledDateValue === undefined ? momentOrNull(paxNode.scheduledFlightDate) : scheduledDateValue;
        const checkInDateDiffers = checkedInDateTime?.isValid() && scheduledFlightDate?.isValid() &&
            checkedInDateTime.format('YYYY-MM-DD') !== scheduledFlightDate.format('YYYY-MM-DD');
        const willCheckIn = !!getCheckInStateUpdate(paxNode, checkInTime,
            canEditCheckInTime && this.props.routerLocation.pathname.startsWith('/app/passengers')).checkInType;
        const isPaxWeightRequired = willCheckIn || isCheckedInPassenger(paxNode);
        const canUpdateFlytCheck = this.props.userGroups.includes(FLYTCHECK_PERMISSION);

        if (paxNode.currentCarrierDoc && !editingDisabled) {

            const flightUrl = `/app/flights/${paxNode.currentCarrierDoc._id}`;

            onFlightAlert = (
                <Alert
                    type="warning"
                    showIcon
                    message="Departure/destination disabled. Passenger is assigned to a flight."
                    description={
                        <span>
                        <Link className="ant-anchor-link" to={flightUrl}>
                            Edit flight '{paxNode.currentCarrierDoc.desig}'
                        </Link>
                    </span>
                    }
                    style={{
                        marginBottom: 12
                    }}
                />
            )
        }

        let flightInput = (
            <Input
                disabled={true}
                value={this.props.initialData.currentCarrierDoc?.desig}
                placeholder="No flight assigned"
                style={{ flex: 1 }}
            />
        )

        if (this.isEditing() && this.props.initialData.currentCarrierDoc && hasPermission){

            const title = <>
                <span>Are you sure you want to remove this pax from the flight?</span><br/>
                <span>This pax will be removed from all flight legs.</span>
            </>

            flightInput = (
                <Input.Group compact style={{ display: 'flex' }}>
                    {flightInput}
                    <Popconfirm
                        title={title}
                        onConfirm={this.onRemoveFromFlight}
                        okText="Remove"
                        okButtonProps={{ type: 'danger' }}
                    >
                        <Button loading={this.props.isRemovingFromFlight}>Remove</Button>
                    </Popconfirm>
                </Input.Group>
            )
        }

        let readOnlyAlert = null;
        if (editingDisabled){
            readOnlyAlert = (
                <Alert
                    type="warning"
                    showIcon
                    message="Read-only mode"
                    description="You are not authorized to make changes"
                    style={{
                        marginBottom: 12
                    }}
                />
            )
        }

        let showCheckedInTime = true;
        if (this.isScheduling()){
            showCheckedInTime = false;
        }


        return <Form style={{ position: 'relative' }}>
            {readOnlyAlert}
            <Form.Item className={FormItemClassNames.NoMargin} label="Current Flight" {...formItemLayout}>
                {flightInput}
            </Form.Item>
            <Divider type="horizontal" style={{ margin: '6px 0' }} />
            <Form.Item className={FormItemClassNames.NoMargin} label="Scheduled Flight Date" {...formItemLayout}>
                {getFieldDecorator('scheduledFlightDate', {
                    initialValue: momentOrNull(this.props.initialData.scheduledFlightDate),
                    rules: [{ required: true, message: 'Scheduled Flight Date is required' }]
                })(
                    <DatePicker disabled={this.isScheduling() || editingDisabled} />
                )}
            </Form.Item>
            {showCheckedInTime && checkInDateDiffers ? (
                <Form.Item className={FormItemClassNames.NoMargin} {...tailFormItemLayout}>
                    <Alert
                        type="warning"
                        showIcon
                        message="The check-in date differs from the scheduled flight date."
                    />
                </Form.Item>
            ) : null}
            <Form.Item className={FormItemClassNames.NoMargin} label="Check-in Type" {...formItemLayout}>
                <Typography.Text>{paxNode.checkInType || 'Unknown'}</Typography.Text>
            </Form.Item>
            {showCheckedInTime ? (
                <Form.Item className={FormItemClassNames.NoMargin} label="Checked In Time" {...formItemLayout}>
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                        {getFieldDecorator('checkedInTime', {
                            initialValue: DateTimePickerValue.from(this.getCheckedInTime()),
                            rules: [ { required: false, validator: DateTimePickerValue.validate } ]
                        })(
                            <DateTimePicker
                                disabled={!canEditCheckInTime || this.props.isUpdating}
                                disabledReason={checkInDisabledReason}
                                datePickerProps={{ format: 'YYYY-MM-DD' }}
                            />
                        )}
                        {this.props.routerLocation.pathname.startsWith('/app/passengers') ? (
                            <Tooltip
                                title={checkInDisabledReason || 'Fills in the current date and time. Click Submit to save.'}
                                trigger={checkInTooltipTriggers}
                            >
                                <span
                                    style={{display: 'inline-block'}}
                                    tabIndex={checkInDisabledReason ? 0 : undefined}
                                    aria-label={checkInDisabledReason ? `Check in now unavailable: ${checkInDisabledReason}` : undefined}
                                >
                                    <Button
                                        size="small"
                                        htmlType="button"
                                        disabled={!!checkInDisabledReason}
                                        onClick={() => {
                                            this.props.form.setFields({
                                                checkedInTime: {value: DateTimePickerValue.from(moment()), touched: true}
                                            });
                                        }}
                                    >Check in now</Button>
                                </span>
                            </Tooltip>
                        ) : null}
                        <Typography.Text type="secondary">
                            {DateTimePickerValue.toMoment(getFieldValue('checkedInTime'))?.utc().format('YYYY-MM-DD HH:mm')}
                            <span> UTC</span>
                        </Typography.Text>
                    </span>
                    {willCheckIn ? (
                        <Alert
                            type="info"
                            showIcon
                            message="Click Submit to check in this passenger. The check-in has not been saved yet."
                        />
                    ) : null}
                </Form.Item>
            ) : null}
            {!this.isScheduling() && canUpdateFlytCheck ? (
                <>
                    <Divider type="horizontal" style={{ margin: '6px 0' }} />
                    <Form.Item className={FormItemClassNames.NoMargin} label="FlytCheck" {...formItemLayout}>
                        <Switch
                            checked={isFlytChecked(paxNode.personID?.credentials)}
                            loading={this.props.isFlytCheckUpdating}
                            disabled={this.props.isFlytCheckUpdating}
                            onChange={this.props.onFlytCheckChange}
                        />
                    </Form.Item>
                </>
            ) : null}
            <Form.Item className={FormItemClassNames.NoMargin} label="Controlling Heliport" {...formItemLayout}>
                {getFieldDecorator('lastKnownController', {
                    initialValue: getLabelInValue(this.props.initialData.lastKnownController, 'name'),
                    rules: [{ required: true, message: 'Controlling Heliport is required' }]
                })(
                    <LocationSelect disabled={this.isScheduling() || editingDisabled} hasDataflytHardware labelInValue/>
                )}
            </Form.Item>
            <Divider type="horizontal" style={{ margin: '6px 0' }} />
            {onFlightAlert}
            <Form.Item className={FormItemClassNames.NoMargin} label="Departure" {...formItemLayout}>
                {getFieldDecorator('departureID', {
                    initialValue: getLabelInValue(this.props.initialData.departureID, 'name'),
                })(
                    <LocationSelect disabled={editingDisabled || isOnFlight} labelInValue/>
                )}
            </Form.Item>
            <Form.Item className={FormItemClassNames.NoMargin} label="Destination" {...formItemLayout}>
                {getFieldDecorator('destinationID', {
                    initialValue: getLabelInValue(this.props.initialData.destinationID, 'name')
                })(
                    <LocationSelect disabled={editingDisabled || isOnFlight} labelInValue/>
                )}
            </Form.Item>
            <Divider type="horizontal" style={{ margin: '6px 0' }} />
            <Form.Item className={FormItemClassNames.NoMargin} label="Charge Code" {...formItemLayout}>
                {getFieldDecorator('chargeCode', {
                    initialValue: this.props.initialData.chargeCode
                })(
                    <Input disabled={editingDisabled} />
                )}
            </Form.Item>
            <Divider type="horizontal" style={{ margin: '6px 0' }} />
            <Row>
                <Col {...formItemLayout.labelCol} />
                <Col {...formItemLayout.wrapperCol}>
                    <Row>
                        <Col sm={6}>
                            <Form.Item label="PAX Weight">
                                {getFieldDecorator('paxWeight', {
                                    initialValue: this.props.initialData.paxWeight,
                                    rules: [{
                                        required: isPaxWeightRequired,
                                        validator: (_rule, value, callback) => {
                                            if (isPaxWeightRequired && (typeof value !== 'number' || !Number.isFinite(value) || value <= 0)) {
                                                callback('Enter a passenger weight greater than zero.');
                                            } else {
                                                callback();
                                            }
                                        }
                                    }]
                                })(
                                    <InputNumber disabled={editingDisabled} min={0} />
                                )}
                            </Form.Item>
                        </Col>
                        <Col sm={6}>
                            <Form.Item label="Bag Weight">
                                {getFieldDecorator('bagWeight', {
                                    initialValue: this.props.initialData.bagWeight
                                })(
                                    <InputNumber disabled={editingDisabled} min={0} />
                                )}
                            </Form.Item>
                        </Col>
                        <Col md={6}>
                            <Form.Item label="Bag Count">
                                {getFieldDecorator('bagCount', {
                                    initialValue: this.props.initialData.bagCount
                                })(
                                    <InputNumber disabled={editingDisabled} min={0} />
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {!editingDisabled ? (
                <Form.Item className={FormItemClassNames.NoMargin} label="" {...tailFormItemLayout}>
                    <Button
                        type="primary"
                        onClick={this.handleFormSubmit}
                        loading={this.props.isUpdating}
                        disabled={this.props.isUpdating || !this.props.form.isFieldsTouched()}
                        size="large"
                    >Submit</Button>
                    {this.props.form.isFieldsTouched() ? (
                        <Button
                            type="link"
                            disabled={this.props.isUpdating}
                            onClick={() => {
                                this.props.form.resetFields();
                            }}
                        >Reset</Button>
                    ) : null}
                </Form.Item>
            ) : null}
        </Form>
    }
}

const CheckInDetails_Form = Form.create<CheckinDetailsProps & InternalFormProps>({
    mapPropsToFields(props){
        if (!props.editingEnabled) return {};
        return props.formData
    },
    onFieldsChange(props, _, allFields){
        if (typeof props.onFormDataChanged === 'function'){
            props.onFormDataChanged(allFields);
        }
    },
    onValuesChange(props, values){
        if (typeof props.onFormValuesChanged === 'function'){
            props.onFormValuesChanged(values);
        }
    }
})(CheckinDetails_Internal)

function FlightCheckInDetails(props: CheckinDetailsProps) {
    const [ userGroups ] = useUserGroups();
    const routerLocation = useLocation();
    const apolloClient = useApolloClient();
    const { sendUpdate, isProcessing } = useSubmit(props.initialData);
    const { remove, loading: removing } = useRemoveFromFlight(props.initialData);
    const [isFlytCheckUpdating, setIsFlytCheckUpdating] = React.useState(false);
    const checkInConfirmation = React.useRef<ReturnType<typeof Modal.confirm> | null>(null);

    React.useEffect(() => {
        return () => {
            checkInConfirmation.current?.destroy();
            checkInConfirmation.current = null;
        };
    }, [props.initialData._id, props.editingEnabled]);

    function onPaxUpdated(paxNode: PaxNodeFields){
        if (typeof props.onPassengerUpdated === 'function'){
            props.onPassengerUpdated(paxNode);
        }
    }

    function handleRemoveFromFlight(){
        remove()
            .then((result) => {
                message.success('Successfully removed pax from flight');
                const paxNodes = result.data.removePaxCgoFromFlight.paxNodes;
                if (Array.isArray(paxNodes) && paxNodes.length > 0){
                    onPaxUpdated(paxNodes[0]);
                }
            })
            .catch((err) => {
                console.log(err);
                message.error('Failed to remove pax from flight. Check console for details.');
            })
    }

    function handleSubmit(form: WrappedFormUtils<PaxNodeFields>){
        if (isProcessing || !props.editingEnabled || !hasEditPermission(routerLocation.pathname, userGroups)) return;

        const originalFormData = {...props.formData};

        form.validateFieldsAndScroll((err: any, values: FormDataValues<FormData>) => {
            if (!err){
                if (checkInConfirmation.current) return;
                const checkedInTime = DateTimePickerValue.toMoment(values.checkedInTime)?.toISOString() ?? null;
                const canEditCheckInTime = !routerLocation.pathname.startsWith('/app/scheduling') && userGroups.includes(CHECK_IN_PERMISSION) && !getCheckInTimeDisabledReason(props.initialData);
                const checkInStateUpdate = getCheckInStateUpdate(props.initialData, checkedInTime,
                    canEditCheckInTime && routerLocation.pathname.startsWith('/app/passengers'));
                const save = async () => {
                    try {
                        const newPaxNode = await sendUpdate({
                            _id: props.initialData._id,
                            tpID: props.initialData.tpID._id,
                            ...(canEditCheckInTime ? {checkedInTime} : {}),
                            ...checkInStateUpdate,
                            scheduledFlightDate: values.scheduledFlightDate?.format('YYYY-MM-DD'),
                            lastKnownController: values.lastKnownController.key,
                            departureID: values.departureID.key,
                            destinationID: values.destinationID.key,
                            paxWeight: values.paxWeight,
                            bagWeight: values.bagWeight,
                            bagCount: values.bagCount,
                            chargeCode: values.chargeCode
                        })
                        message.success('Successfully updated passenger check-in details.');
                        onPaxUpdated(newPaxNode);

                        form.resetFields();
                    }
                    catch(err){
                        message.error("Failed to update passenger check-in details.");
                        console.error(err);
                        // Passenger and flight writes can succeed independently. Refresh both the
                        // editor cache and the live list while preserving the user's form edits.
                        try {
                            const refreshed = await apolloClient.query<{getPassenger: PaxNodeFields}>({
                                query: PassengerQuery,
                                variables: {paxId: props.initialData._id},
                                fetchPolicy: 'network-only'
                            });
                            if (refreshed.data?.getPassenger) onPaxUpdated(refreshed.data.getPassenger);
                        } catch (refreshError) {
                            message.warning('Could not refresh passenger details. Reload before retrying.');
                        }
                        if (typeof props.onFormDataChanged === 'function'){
                            // Reset back original form data if it fails to upload
                            props.onFormDataChanged(originalFormData);
                            return;
                        }
                    }
                };
                if (checkInStateUpdate.checkInType === 'CSR') {
                    checkInConfirmation.current = Modal.confirm({
                        title: 'Are you sure?',
                        content: `This passenger will change from ${props.initialData.checkInType || 'Unknown'} to checked in.`,
                        okText: 'Check in passenger',
                        cancelText: 'Cancel',
                        onOk: async () => {
                            try { await save(); }
                            finally { checkInConfirmation.current = null; }
                        },
                        onCancel: () => { checkInConfirmation.current = null; }
                    });
                } else {
                    save();
                }
            }
        });
    }

    async function handleFlytCheckChange(checked: boolean){
        setIsFlytCheckUpdating(true);
        try {
            const result = await apolloClient.mutate<{ setFlytCheckStatus: boolean }>({
                mutation: SetFlytCheckStatus,
                variables: {
                    paxNodeID: props.initialData._id,
                    checked
                }
            });
            if (result.data?.setFlytCheckStatus !== checked) {
                throw new Error('FlytCheck update returned an unexpected state');
            }

            const refreshed = await apolloClient.query<{ getPassenger: PaxNodeFields }>({
                query: PassengerQuery,
                variables: {paxId: props.initialData._id},
                fetchPolicy: 'network-only'
            });
            const updatedPaxNode = refreshed.data?.getPassenger;
            if (!updatedPaxNode) {
                throw new Error('FlytCheck update could not refresh the passenger');
            }
            message.success(`Passenger FlytCheck status ${checked ? 'set' : 'cleared'}.`);
            onPaxUpdated(updatedPaxNode);
        } catch (error) {
            console.error(error);
            message.error('Failed to update passenger FlytCheck status.');
        } finally {
            setIsFlytCheckUpdating(false);
        }
    }

        return <CheckInDetails_Form
            {...props}
            isUpdating={isProcessing}
            isFlytCheckUpdating={isFlytCheckUpdating}
            onFormDataChanged={props.onFormDataChanged}
            onFormSubmit={handleSubmit}
            onFlytCheckChange={handleFlytCheckChange}
            onRemoveFromFlight={handleRemoveFromFlight}
            isRemovingFromFlight={removing}
            userGroups={userGroups}
            routerLocation={routerLocation}
        />
    }

export default FlightCheckInDetails;
